import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";

import { normalizza } from "../ancoraggio";

import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";


// Il worker di pdf.js servito dal nostro bundle, non da un CDN: lo strumento
// deve funzionare anche su un box senza rete.
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    "pdfjs-dist/build/pdf.worker.min.mjs",
    import.meta.url,
).toString();


interface Props
{
    pdf: Uint8Array<ArrayBuffer> | null;
}


export type EsitoSalto = "trovato" | "non-trovato" | "non-pronto";

export interface Comandi
{
    vaiAlTesto: (frase: string) => Promise<EsitoSalto>;
}


interface Segno
{
    top: number;
    altezza: number;
    /** Cambia a ogni salto: fa ripartire l'animazione anche sullo stesso punto. */
    volta: number;
}


export const PdfView = forwardRef<Comandi, Props>(function PdfView({ pdf }, comandi): React.JSX.Element
{
    const [pages, setPages] = useState(0);
    const [width, setWidth] = useState(760);
    const [zoom, setZoom] = useState(1);
    const [guasto, setGuasto] = useState("");
    const [segno, setSegno] = useState<Segno | null>(null);
    const contenitore = useRef<HTMLDivElement>(null);
    const documento = useRef<pdfjs.PDFDocumentProxy | null>(null);
    const volta = useRef(0);

    // Un URL di blob, NON i byte grezzi. Passando `{ data: bytes }` pdf.js
    // TRASFERISCE l'ArrayBuffer al suo worker: il buffer resta staccato, e la
    // seconda apertura dello stesso documento — che in React 19 arriva sempre,
    // perche' StrictMode monta due volte — trova un buffer vuoto e fallisce.
    // Il sintomo era «questo PDF non si apre» senza un errore in console:
    // il PDF era buono, i byte no.
    const file = useMemo(
        () => (pdf === null ? null : URL.createObjectURL(new Blob([pdf], { type: "application/pdf" }))),
        [pdf],
    );

    // L'URL vive quanto il documento: senza revoca il blob resta in memoria a
    // ogni composizione, e questo strumento si usa per ore.
    useEffect(() =>
    {
        setGuasto("");
        setPages(0);
        return () => { if (file !== null) { URL.revokeObjectURL(file); } };
    }, [file]);

    // Il salto: si cerca la frase nel TESTO del PDF, pagina per pagina, e si
    // porta lo scorrimento dove sta.
    //
    // Il testo di una pagina arriva a pezzi (`items`), spezzati dove al
    // compositore fa comodo: si concatenano prima di cercare, tenendo da parte
    // dove comincia ciascun pezzo, così dal punto trovato si risale al pezzo e
    // dal pezzo alla sua altezza sulla pagina.
    useImperativeHandle(comandi, () => ({
        vaiAlTesto: async (frase: string): Promise<EsitoSalto> =>
        {
            const doc = documento.current;
            const box = contenitore.current;

            if (doc === null || box === null) { return "non-pronto"; }

            const cercata = normalizza(frase);

            for (let n = 1; n <= doc.numPages; n++)
            {
                const pagina = await doc.getPage(n);
                const contenuto = await pagina.getTextContent();

                // Si concatena GIÀ normalizzato, segnando dove comincia ogni
                // pezzo nella stringa normalizzata. Normalizzare dopo aver
                // concatenato accorcia il testo e sposta gli indici: la frase
                // veniva trovata giusta ma attribuita al pezzo sbagliato, e la
                // fascia finiva due paragrafi più su. (Misurato: la sonda
                // legge il testo del PDF sotto la fascia, non il fatto che lo
                // scorrimento si sia mosso.)
                let steso = "";
                const inizi: { da: number; item: (typeof contenuto.items)[number] }[] = [];

                for (const item of contenuto.items)
                {
                    if (!("str" in item)) { continue; }

                    const ripulito = normalizza(item.str);

                    if (ripulito === "") { continue; }

                    inizi.push({ da: steso.length, item });
                    steso += `${ripulito} `;
                }

                const dove = steso.indexOf(cercata);

                if (dove === -1) { continue; }

                const pezzo = [...inizi].reverse().find((i) => i.da <= dove) ?? inizi[0];
                const elemento = box.querySelector<HTMLElement>(`.react-pdf__Page[data-page-number="${n}"]`);

                if (pezzo === undefined || elemento === null) { return "non-trovato"; }

                const vista = pagina.getViewport({ scale: 1 });
                const scala = elemento.clientHeight / vista.height;

                // Nel PDF l'origine sta in basso a sinistra, sullo schermo in
                // alto a sinistra: la y va rovesciata.
                const transform = "transform" in pezzo.item ? pezzo.item.transform : [0, 0, 0, 0, 0, 0];
                const altezzaRiga = ("height" in pezzo.item ? pezzo.item.height : 12) * scala;
                const dentro = (vista.height - (transform[5] ?? 0)) * scala;

                volta.current += 1;
                setSegno({ top: elemento.offsetTop + dentro - altezzaRiga, altezza: Math.max(14, altezzaRiga * 1.4), volta: volta.current });

                box.scrollTo({
                    top: Math.max(0, elemento.offsetTop + dentro - box.clientHeight / 2),
                    behavior: "smooth",
                });

                return "trovato";
            }

            return "non-trovato";
        },
    }), []);


    // La pagina e' larga quanto il CONTENITORE, misurato. La prima stesura la
    // deduceva dalla finestra (`innerWidth / 2 - 80`): una formula che
    // indovina il layout invece di guardarlo, e che su uno schermo stretto —
    // dove il pannello e' largo quanto tutta la finestra — sbagliava di
    // meta'. Un ResizeObserver dice la verita' in ogni layout, anche futuro.
    useEffect(() =>
    {
        const box = contenitore.current;
        if (box === null) { return; }

        const osserva = new ResizeObserver(([voce]) =>
        {
            if (voce) { setWidth(Math.max(240, Math.min(980, voce.contentRect.width - 32))); }
        });
        osserva.observe(box);
        return () => osserva.disconnect();
    }, [file]);

    if (file === null)
    {
        return (
            <div className="vuoto">
                <p>Nessuna anteprima ancora.</p>
                <p className="fioco">Scrivi a sinistra, poi <kbd>Ctrl</kbd>+<kbd>↵</kbd> o «Componi».</p>
            </div>
        );
    }

    return (
        <>
            <div className="barra">
                <span className="conta">{pages} {pages === 1 ? "pagina" : "pagine"}</span>
                <span className="spazio" />
                <button type="button" onClick={() => setZoom((z) => Math.max(0.4, z - 0.1))}>−</button>
                <span className="conta">{Math.round(zoom * 100)}%</span>
                <button type="button" onClick={() => setZoom((z) => Math.min(2.5, z + 0.1))}>+</button>
                <button type="button" onClick={() => setZoom(1)}>reset</button>
            </div>
            <div className="pagine" ref={contenitore}>
                {segno !== null && (
                    <div
                        key={segno.volta}
                        className="segno-salto"
                        style={{ top: `${segno.top}px`, height: `${segno.altezza}px` }}
                    />
                )}
                <Document
                    file={file}
                    onLoadSuccess={(doc) => { documento.current = doc; setPages(doc.numPages); }}
                    onLoadError={(err) => setGuasto(err.message)}
                    loading={<p className="fioco">apro il PDF…</p>}
                    error={<p className="fioco">questo PDF non si apre — {guasto || "motivo non riportato"}</p>}
                >
                    {Array.from({ length: pages }, (_unused, i) => (
                        <Page
                            key={i}
                            pageNumber={i + 1}
                            width={width * zoom}
                            renderAnnotationLayer
                            renderTextLayer
                        />
                    ))}
                </Document>
            </div>
        </>
    );
});
